import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';

const CoveragesPage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Extra Coverage & Extended Warranties | EquipEASE Lease Co.</title>
        <meta name="description" content="Protect your equipment investment with EquipEASE extended warranties and payment protection coverage. Heavy-duty truck warranties and life insurance coverage available." />
        <link rel="canonical" href="https://equipease.ca/coverages" />
      </Helmet>
      
      <Header />
      
      <section className="flex-1 py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Page Header */}
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 text-[#333333]">Extra Coverages</h1>
              <div className="w-20 h-1 bg-[#FF6600] mx-auto mb-6"></div>
              <p className="text-xl text-[#4A4A4A]">
                Comprehensive protection plans to keep your business running smoothly
              </p>
            </div>

            {/* Part 1: Extended Warranties */}
            <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 mb-12">
              <h2 className="text-3xl font-bold mb-6 text-[#333333]">
                Heavy and Medium Duty Truck Extended Warranties
              </h2>
              
              <h3 className="text-2xl font-bold mb-4 text-[#FF6600]">
                Comprehensive Guide to EquipEASE's Extended Warranty Plans
              </h3>
              
              <p className="text-lg font-semibold mb-6 text-[#4A4A4A]">
                EquipEASE Protection Powered by Premium 2000+®
              </p>

              <div className="prose prose-lg max-w-none text-[#4A4A4A] space-y-6">
                <p>
                  At EquipEASE, we understand that your equipment is the heart of your business. That's why we're proud to offer reliable extended warranty coverage through our trusted partner, <strong>Premium 2000+®</strong> — a leader in aftermarket warranty programs for medium- and heavy-duty used commercial trucks for over 25 years.
                </p>

                <p>
                  Whether you operate a single unit or a small fleet, EquipEASE clients trust that when a covered failure occurs, we'll stand behind the warranty — not hide behind it. If the issue is covered, the repair gets paid. It's that simple. In fact, Premium 2000+® pays out tens of millions in repair claims each year to keep hardworking operators like you safely on the road with minimal downtime.
                </p>

                <p>
                  Let's face it: when you own or purchase used equipment, mechanical failure isn't a matter of <em>if</em>, but <em>when</em>. Some issues may be minor and affordable — but others can be costly, unexpected, and financially overwhelming. That's why securing a warranty backed by a proven track record is one of the smartest investments you can make in your business.
                </p>

                <p>
                  Should something go wrong, help is always within reach. You'll have access to your own customer portal, along with our responsive service team via phone or email. No matter where you're located, we'll connect you with one of <strong>5,000+ approved repair facilities</strong> across North America and get your covered repairs underway quickly and efficiently.
                </p>

                <p className="text-lg font-semibold text-[#333333]">
                  At EquipEASE, your uptime is our priority. This isn't just about repairs — it's about keeping your business moving forward, with confidence.
                </p>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a 
                  href="tel:1-844-250-3273"
                  className="bg-[#FF6600] hover:bg-[#FF9933] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors shadow-lg text-center"
                >
                  Call for Warranty Quote
                </a>
                <Link
                  to="/#contact"
                  className="bg-[#4A4A4A] hover:bg-[#333333] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors shadow-lg text-center"
                >
                  Request Information
                </Link>
              </div>
            </div>

            {/* Part 2: Payment Protection Coverage */}
            <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
              <h2 className="text-3xl font-bold mb-6 text-[#333333]">
                Payment Protection Coverage
              </h2>
              
              <p className="text-xl mb-8 text-[#4A4A4A]">
                Protect your <strong>NEW</strong> or <strong>EXISTING</strong> lease from life's unexpected events.
              </p>

              {/* Life Benefit */}
              <div className="mb-8 p-6 bg-gradient-to-r from-[#FF6600]/5 to-[#FF9933]/10 rounded-lg border-l-4 border-[#FF6600]">
                <h3 className="text-2xl font-bold mb-4 text-[#333333]">Life Benefit</h3>
                <p className="text-lg mb-4 text-[#4A4A4A]">
                  The balance owing at date of death is paid to the creditor. This provides your family with an asset as opposed to a liability.
                </p>
                <div className="bg-white p-4 rounded-lg mt-4">
                  <h4 className="font-semibold text-[#FF6600] mb-3">Full Details:</h4>
                  <ul className="list-disc pl-6 space-y-2 text-[#4A4A4A]">
                    <li>Guaranteed Issue up to $250,000</li>
                    <li>Life Benefit covers both accidental and sickness/illness related deaths</li>
                  </ul>
                </div>
              </div>

              {/* Life with Accidental Disability */}
              <div className="mb-8 p-6 bg-gradient-to-r from-[#4A4A4A]/5 to-[#333333]/10 rounded-lg border-l-4 border-[#4A4A4A]">
                <h3 className="text-2xl font-bold mb-4 text-[#333333]">Life with Accidental Disability</h3>
                <div className="bg-white p-4 rounded-lg">
                  <ul className="list-disc pl-6 space-y-2 text-[#4A4A4A]">
                    <li>Guaranteed Issue up to $250,000 for Life Coverage</li>
                    <li>Guaranteed Issue up to $6,000 per month for Disability Coverage</li>
                    <li>Qualified clients can obtain up to $6,000 per month of Accidental Disability Coverage</li>
                    <li>Life Benefit covers both Accidental & Sickness/Illness related deaths</li>
                    <li>Accidental Disability covers accident only related disabilities for a maximum of 6 month benefit</li>
                  </ul>
                </div>
              </div>

              {/* Disability Benefit */}
              <div className="mb-8 p-6 bg-gradient-to-r from-[#FF6600]/5 to-[#FF9933]/10 rounded-lg border-l-4 border-[#FF6600]">
                <h3 className="text-2xl font-bold mb-4 text-[#333333]">Disability Benefit</h3>
                <p className="text-lg mb-4 text-[#4A4A4A]">
                  Accidents and illnesses are a part of life; they can happen to anyone at any time.
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-4 text-[#4A4A4A]">
                  <li>If eligible, your monthly payment is made on your behalf during the selected term of coverage</li>
                  <li>Allows you to maintain your credit rating</li>
                  <li>Monthly benefit up to $6,000 per month</li>
                  <li>Eligibility up to age 65</li>
                </ul>
                <div className="bg-white p-4 rounded-lg mt-4">
                  <h4 className="font-semibold text-[#FF6600] mb-3">Full Details:</h4>
                  <ul className="list-disc pl-6 space-y-2 text-[#4A4A4A]">
                    <li>Guaranteed Issue up to $6,000 per month</li>
                    <li>Qualified clients can obtain up to $6,000 per month of Disability Coverage for a term of 6 months, 12 months, or 24 months provided their age does not exceed 65</li>
                    <li>Accidental & Sickness/Illness related disabilities are covered</li>
                    <li>Available on a "Stand-Alone Basis" (i.e. not required to take Life Coverage in order to obtain Disability Coverage Protection)</li>
                    <li><strong>30-Day Elimination offered:</strong> Provides protection after a waiting period of 30 days. Customer must be disabled for 30 consecutive days and the benefit paid on day 31.</li>
                  </ul>
                </div>
              </div>

              {/* Critical Illness Benefit */}
              <div className="mb-8 p-6 bg-gradient-to-r from-[#4A4A4A]/5 to-[#333333]/10 rounded-lg border-l-4 border-[#4A4A4A]">
                <h3 className="text-2xl font-bold mb-4 text-[#333333]">Critical Illness Benefit</h3>
                <p className="text-sm italic mb-4 text-[#4A4A4A]">
                  (only available in conjunction with the purchase of Life Coverage)
                </p>
                <p className="text-lg mb-4 text-[#4A4A4A]">
                  Provides a payment if you are diagnosed with a covered critical illness and survive for at least 30 days.
                </p>
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-[#4A4A4A] mb-3">Full Details:</h4>
                  <ul className="list-disc pl-6 space-y-2 text-[#4A4A4A]">
                    <li>Guaranteed Issue up to a maximum of $6,000 per month</li>
                    <li>Maximum benefit of $18,000</li>
                    <li>Critical Illness covers Life Threatening Cancer, Heart Attack, Stroke, Coronary Artery Bypass Graft, Kidney Failure and Major Organ transplant</li>
                  </ul>
                </div>
              </div>

              {/* Involuntary Unemployment Benefit */}
              <div className="mb-8 p-6 bg-gradient-to-r from-[#FF6600]/5 to-[#FF9933]/10 rounded-lg border-l-4 border-[#FF6600]">
                <h3 className="text-2xl font-bold mb-4 text-[#333333]">Involuntary Unemployment Benefit</h3>
                <ul className="list-disc pl-6 space-y-2 text-[#4A4A4A]">
                  <li>Unemployment coverage is available for both employed and self employed applicants (*conditions may apply – please speak to our representative for more information)</li>
                  <li>Guaranteed Issue up to $6,000 per month</li>
                  <li>Involuntary Unemployment covers job loss up to 12 months per occurrence</li>
                  <li>Monthly Pay Option includes premium waiver benefit</li>
                  <li className="text-sm italic">Some conditions may apply - see more details in attachments</li>
                </ul>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a 
                  href="tel:1-844-250-3273"
                  className="bg-[#FF6600] hover:bg-[#FF9933] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors shadow-lg text-center"
                >
                  Call for Coverage Quote
                </a>
                <Link
                  to="/#contact"
                  className="bg-[#4A4A4A] hover:bg-[#333333] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors shadow-lg text-center"
                >
                  Request Information
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default CoveragesPage;
